import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ArrowDown, ArrowUp, Printer, ShieldOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  createFailureReview, downloadBrochurePdf, downloadMaturityPdf, getBrochure, getBrochureIssues, getLaunch,
  getMeasures, getMetrics, getMgmtAudits, saveLaunch, saveMeasure, saveMgmtAudit, setBrochureIssueStatus,
  submitMaturity, LEVEL_TONE,
} from './api';

const Stat = ({ label, value, tid, hint }) => (
  <div className="rounded-xl border border-muted/60 bg-card p-4" data-testid={tid}>
    <p className="text-xl font-bold tabular-nums text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
    {hint && <p className="mt-0.5 text-[10px] text-muted-foreground/80">{hint}</p>}
  </div>
);

const Section = ({ title, hint, children, tid }) => (
  <Card data-testid={tid}>
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

// ─── Learning — how to fill it in, and what to fill in ───────────────
export const LearningTab = ({ content, onGo }) => {
  const [open, setOpen] = useState('know');
  return (
    <div className="space-y-5" data-testid="method-learning">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Before you start — eight steps</CardTitle>
          <p className="text-xs text-muted-foreground">
            Everyone should have the same foundation before the first condition is set. Work down this list once.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.learning_start.map((s) => (
            <div key={s.step} className="flex gap-3 rounded-lg border border-muted/60 p-3" data-testid={`method-learn-start-${s.step}`}>
              <span className="font-display text-lg font-bold text-primary/60">{s.step}</span>
              <p className="text-[11px] text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Every tab, field by field</CardTitle>
          <p className="text-xs text-muted-foreground">
            What the tab is for, when to use it, what goes in each field — with an example and the rule the platform enforces.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.learning_guide.map((g) => (
            <div key={g.key} className="rounded-lg border border-muted/60" data-testid={`method-learn-${g.key}`}>
              <button onClick={() => setOpen(open === g.key ? '' : g.key)}
                      className="flex w-full items-center justify-between gap-3 p-3 text-left cursor-pointer"
                      data-testid={`method-learn-toggle-${g.key}`}>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {g.move !== '—' && <span className="mr-2 font-display text-primary">{g.move}</span>}{g.tab}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{g.purpose}</p>
                </div>
                <span className="shrink-0 text-[11px] text-primary">{open === g.key ? 'Hide' : 'Show'}</span>
              </button>
              {open === g.key && (
                <div className="space-y-2 border-t border-muted/60 p-3">
                  <p className="text-[11px] text-muted-foreground"><strong className="text-foreground">When</strong> {g.when}</p>
                  {g.fill.map((f) => (
                    <div key={f.field} className="rounded-lg border border-muted/60 p-2.5" data-testid={`method-learn-field-${g.key}-${f.field.slice(0, 8).toLowerCase().replace(/[^a-z]/g, '')}`}>
                      <p className="text-xs font-semibold text-foreground">{f.field}</p>
                      <p className="text-[11px] text-muted-foreground">{f.what}</p>
                      <p className="mt-1 text-[11px] italic text-muted-foreground">Example: {f.example}</p>
                      <p className="text-[11px] text-primary">Rule: {f.rule}</p>
                    </div>
                  ))}
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2.5">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-rose-700">Common mistakes</p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-[11px] text-muted-foreground">
                      {g.mistakes.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </div>
                  {onGo && g.key !== 'today' && (
                    <Button size="sm" variant="outline" onClick={() => onGo(g.key)} data-testid={`method-learn-open-${g.key}`}>
                      Open {g.tab}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Words that must mean the same thing</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {content.learning_glossary.map((g) => (
            <div key={g.term} className="rounded-lg border border-muted/60 p-3" data-testid={`method-glossary-${g.term.toLowerCase().replace(/ /g, '-')}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{g.term}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{g.text}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">The four levels and the five work states</CardTitle>
          <p className="text-xs text-muted-foreground">If these two scales are not used identically by everyone, nothing else in the method works.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {content.response_ladder.map((l) => (
              <div key={l.level} className={`rounded-lg border p-3 ${LEVEL_TONE[l.level]}`} data-testid={`method-learn-level-${l.level}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide">{l.label} — {l.condition}</p>
                <p className="text-[11px] opacity-90">{l.response}</p>
                <p className="text-[11px] opacity-70">e.g. {l.example}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {content.work_state_definitions.map((s) => (
              <div key={s.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-learn-state-${s.key}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{s.letter} · {s.name}</p>
                <p className="text-[10px] text-muted-foreground">{s.text}</p>
                <p className="mt-1 text-[10px] text-foreground">{s.movable ? 'Counts as workload now' : 'Shown separately'}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {content.evidence_sources.map((s) => (
              <div key={s.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-learn-source-${s.key}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{s.label}</p>
                <p className="text-[10px] text-muted-foreground">{s.hint}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Who does what</CardTitle>
          <p className="text-xs text-muted-foreground">One method, different authority. Nobody is asked to own a decision outside their role.</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs" data-testid="method-learn-roles">
            <thead><tr className="text-muted-foreground">{['Role', 'Know', 'Align', 'Navigate / Respond', 'Integrate'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {content.roles.map((r) => (
                <tr key={r.role} className="border-t border-muted/60 align-top">
                  <td className="px-2 py-2 font-semibold text-foreground">{r.role}</td>
                  <td className="px-2 py-2 text-muted-foreground">{r.know}</td>
                  <td className="px-2 py-2 text-muted-foreground">{r.align}</td>
                  <td className="px-2 py-2 text-muted-foreground">{r.navigate_respond}</td>
                  <td className="px-2 py-2 text-muted-foreground">{r.integrate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Metrics — result measures and control behaviour side by side ─────
export const MetricsTab = ({ content }) => {
  const [data, setData] = useState(null);
  const [measures, setMeasures] = useState(null);
  const [days, setDays] = useState(30);
  const [form, setForm] = useState({ period: new Date().toISOString().slice(0, 7), measure: 'otif', baseline: '', current: '', note: '' });

  const load = useCallback(async () => {
    const to = new Date();
    const from = new Date(to.getTime() - (days - 1) * 86400000);
    setData(await getMetrics(from.toISOString().slice(0, 10), to.toISOString().slice(0, 10)));
    setMeasures(await getMeasures());
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await saveMeasure({ ...form, baseline: Number(form.baseline), current: Number(form.current) });
      toast.success('Measure updated');
      setForm((p) => ({ ...p, baseline: '', current: '', note: '' }));
      await load();
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading metrics…</p>;
  const { know, align, navigate, respond, integrate, rhythm, management, maturity } = data;

  return (
    <div className="space-y-5" data-testid="method-metrics">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors cursor-pointer ${days === d ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-muted text-muted-foreground hover:text-foreground'}`}
                  data-testid={`method-metrics-range-${d}`}>Last {d} days</button>
        ))}
        <span className="text-[11px] text-muted-foreground">{data.date_from} → {data.date_to}</span>
      </div>

      <p className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-[11px] text-foreground" data-testid="method-measurement-discipline">
        <strong className="uppercase tracking-wide text-primary">Measurement discipline</strong> — {content.measurement_discipline}
      </p>

      <Section title="K · Know — is the condition actually visible?" tid="method-metrics-know">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Condition coverage" value={`${know.coverage_percent}%`} tid="method-metric-coverage" hint={`${know.days_covered} of ${data.days} days`} />
          <Stat label="Backlog overstatement" value={`${know.avg_backlog_overstatement_percent}%`} tid="method-metric-overstatement" hint={`${know.held_units} units held vs ${know.movable_units} movable`} />
          <Stat label="Assumption share" value={`${know.assumption_share_percent}%`} tid="method-metric-assumption" hint="of all non-green evidence" />
          <Stat label="Most abnormal dimension" value={(know.most_frequent_abnormal_dimension || '—').toUpperCase()} tid="method-metric-worst-dim" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-xs">
            <thead><tr className="text-muted-foreground">{['Dimension', 'Green', 'Amber', 'Red', 'Black'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {content.condition_dimensions.map((d) => (
                <tr key={d.key} className="border-t border-muted/60" data-testid={`method-metric-dim-${d.key}`}>
                  <td className="px-2 py-1.5 font-medium text-foreground">{d.name}</td>
                  {['green', 'amber', 'red', 'black'].map((l) => (
                    <td key={l} className="px-2 py-1.5 tabular-nums text-muted-foreground">{know.dimension_levels[d.key]?.[l] ?? 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="A · Align" tid="method-metrics-align">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Days with direction" value={`${align.coverage_percent}%`} tid="method-metric-align-coverage" />
            <Stat label="Check-back recorded" value={`${align.check_back_percent}%`} tid="method-metric-checkback" hint={`${align.huddles_complete} complete huddles`} />
          </div>
        </Section>
        <Section title="N · Navigate" tid="method-metrics-navigate">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Routes walked" value={navigate.routes} tid="method-metric-routes" hint={`${navigate.digital_routes} digital`} />
            <Stat label="Average route" value={`${navigate.avg_minutes} min`} tid="method-metric-route-minutes" hint={`${navigate.within_15_minutes_percent}% within 15 min`} />
          </div>
        </Section>
        <Section title="R · Respond" tid="method-metrics-respond">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Abnormalities" value={respond.abnormalities} tid="method-metric-abnormalities" hint={Object.entries(respond.by_level).map(([k, v]) => `${k} ${v}`).join(' · ')} />
            <Stat label="With a boundary" value={`${respond.with_boundary_percent}%`} tid="method-metric-boundary" />
            <Stat label="Containment breaches" value={respond.containment_breaches} tid="method-metric-breaches" hint={`${respond.containments} containments used`} />
            <Stat label="Average time to close" value={`${respond.avg_hours_to_close}h`} tid="method-metric-close-hours" hint={`${respond.open} still open`} />
          </div>
        </Section>
        <Section title="I · Integrate" tid="method-metrics-integrate">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Changes recorded" value={integrate.changes} tid="method-metric-changes" />
            <Stat label="Held on verification" value={`${integrate.held_percent}%`} tid="method-metric-held" hint={`${integrate.reopened} reopened`} />
            <Stat label="Verification overdue" value={integrate.verification_overdue} tid="method-metric-overdue" />
            <Stat label="Rhythm adherence" value={`${rhythm.adherence_percent}%`} tid="method-metric-rhythm" hint={`${rhythm.checks_done} of ${rhythm.expected}`} />
          </div>
        </Section>
      </div>

      <Section title="Business impact — result measures" tid="method-metrics-impact"
               hint="KANRI does not promise percentage gains. Record the measure, and the platform checks whether it is moving in the direction the mechanism predicts.">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <select value={form.measure} onChange={(e) => setForm((p) => ({ ...p, measure: e.target.value }))}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="method-measure-select">
            {content.business_impact.map((m) => <option key={m.key} value={m.key}>{m.measure}</option>)}
          </select>
          <Input value={form.period} onChange={(e) => setForm((p) => ({ ...p, period: e.target.value }))}
                 placeholder="Period (e.g. 2026-06)" className="h-9 text-sm" data-testid="method-measure-period" />
          <Input value={form.baseline} onChange={(e) => setForm((p) => ({ ...p, baseline: e.target.value }))}
                 placeholder="Baseline" type="number" className="h-9 text-sm" data-testid="method-measure-baseline" />
          <Input value={form.current} onChange={(e) => setForm((p) => ({ ...p, current: e.target.value }))}
                 placeholder="Current" type="number" className="h-9 text-sm" data-testid="method-measure-current" />
        </div>
        <p className="text-[11px] text-muted-foreground">
          <strong className="text-foreground">Mechanism</strong> {content.business_impact.find((m) => m.key === form.measure)?.mechanism}
          {' · '}<strong className="text-foreground">Expected</strong> {content.business_impact.find((m) => m.key === form.measure)?.direction}
        </p>
        <Button size="sm" onClick={save} disabled={form.baseline === '' || form.current === ''} data-testid="method-measure-save">Record measure</Button>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs">
            <thead><tr className="text-muted-foreground">{['Measure', 'Period', 'Baseline', 'Current', 'Change', 'Direction'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {(measures?.rows || []).length === 0 && (
                <tr><td colSpan={6} className="px-2 py-3 text-muted-foreground">No measures recorded yet.</td></tr>
              )}
              {(measures?.rows || []).map((r) => {
                const spec = content.business_impact.find((m) => m.key === r.measure);
                return (
                  <tr key={`${r.period}-${r.measure}`} className="border-t border-muted/60" data-testid={`method-measure-row-${r.measure}`}>
                    <td className="px-2 py-1.5 font-medium text-foreground">{spec?.measure}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{r.period}</td>
                    <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{r.baseline}</td>
                    <td className="px-2 py-1.5 tabular-nums text-foreground">{r.current}</td>
                    <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                      {r.delta > 0 ? '+' : ''}{r.delta}{r.delta_percent !== null ? ` (${r.delta_percent}%)` : ''}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge className={`text-[10px] border ${r.moving_in_expected_direction ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700' : 'border-rose-500/40 bg-rose-500/15 text-rose-700'}`}>
                        {r.moving_in_expected_direction ? <ArrowUp className="mr-1 h-3 w-3" /> : <ArrowDown className="mr-1 h-3 w-3" />}
                        {r.moving_in_expected_direction ? 'as predicted' : 'against expectation'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Maturity and management" tid="method-metrics-maturity">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Maturity score" value={maturity.latest ? `${maturity.latest.score}/20` : '—'} tid="method-metric-maturity" hint={maturity.latest?.band} />
          <Stat label="Weakest move" value={maturity.latest?.weakest_move || '—'} tid="method-metric-weakest" />
          <Stat label="Management audits" value={management.audits} tid="method-metric-audits" />
          <Stat label="Management findings" value={management.total_findings} tid="method-metric-findings" />
        </div>
      </Section>
    </div>
  );
};

// ─── Management stability audit ───────────────────────────────────────
export const ManagementTab = ({ content, date }) => {
  const [checks, setChecks] = useState({});
  const [selfCheck, setSelfCheck] = useState({});
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const d = await getMgmtAudits(date, date);
    setRows(d.rows);
    setChecks(d.rows[0]?.checks || {});
    setSelfCheck(d.rows[0]?.self_check || {});
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const set = (key, patch) => setChecks((p) => ({ ...p, [key]: { found: false, evidence: '', leader_response: '', ...(p[key] || {}), ...patch } }));

  const save = async () => {
    try { await saveMgmtAudit({ date, checks, self_check: selfCheck }); toast.success('Audit recorded'); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-management">
      <p className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-[11px] text-foreground" data-testid="method-no-immunity">
        <strong className="uppercase tracking-wide text-primary">No immunity</strong> — {content.no_immunity}
      </p>

      <Section title={`Management stability audit · ${date}`} tid="method-mgmt-audit"
               hint="KANRI fails when standard work is demanded downward while volatility and late decisions are generated upward. A finding needs evidence and the leader response that follows it.">
        {content.mgmt_audit_checks.map((c) => {
          const cur = checks[c.key] || {};
          return (
            <div key={c.key} className={`rounded-lg border p-3 ${cur.found ? 'border-amber-500/40 bg-amber-500/5' : 'border-muted/60'}`} data-testid={`method-mgmt-${c.key}`}>
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" className="mt-1" checked={!!cur.found}
                       onChange={(e) => set(c.key, { found: e.target.checked })}
                       data-testid={`method-mgmt-check-${c.key}`} />
                <div>
                  <p className="text-xs font-semibold text-foreground">{c.check}</p>
                  <p className="text-[11px] text-muted-foreground">{c.question}</p>
                  <p className="text-[11px] text-muted-foreground/80">Evidence needed: {c.evidence}</p>
                  <p className="text-[11px] text-primary">→ {c.leader_response}</p>
                </div>
              </label>
              {cur.found && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <Input value={cur.evidence || ''} onChange={(e) => set(c.key, { evidence: e.target.value })}
                         placeholder="Evidence" className="h-8 text-xs" data-testid={`method-mgmt-evidence-${c.key}`} />
                  <Input value={cur.leader_response || ''} onChange={(e) => set(c.key, { leader_response: e.target.value })}
                         placeholder="Leader response" className="h-8 text-xs" data-testid={`method-mgmt-response-${c.key}`} />
                </div>
              )}
            </div>
          );
        })}
        <Button size="sm" onClick={save} data-testid="method-mgmt-save">Record audit</Button>
      </Section>

      <Section title="Management self-check" tid="method-mgmt-self">
        {content.mgmt_self_check.map((q, i) => (
          <label key={q} className="block">
            <span className="text-xs font-medium text-foreground">{q}</span>
            <Input value={selfCheck[i] || ''} onChange={(e) => setSelfCheck((p) => ({ ...p, [i]: e.target.value }))}
                   className="mt-1 h-8 text-xs" data-testid={`method-mgmt-self-${i}`} />
          </label>
        ))}
        {rows.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="method-mgmt-last">
            Last recorded {(rows[0].updated_at || '').slice(0, 16).replace('T', ' ')} · {rows[0].findings_found?.length || 0} finding(s)
          </p>
        )}
      </Section>
    </div>
  );
};

// ─── First-shift launch ──────────────────────────────────────────────
export const LaunchCard = ({ content }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ flow: '', deadline: '', coordinator: '' });

  const load = useCallback(async () => {
    const d = await getLaunch();
    setData(d);
    setForm({ flow: d.record.flow || '', deadline: d.record.deadline || '', coordinator: d.record.coordinator || '' });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch = {}) => {
    try {
      setData(await saveLaunch({
        ...form, steps: data?.record?.steps || {}, week_one: data?.record?.week_one || {}, ...patch,
      }));
      toast.success('Launch updated');
    } catch (e) { toast.error(e.message); }
  };

  const toggle = (group, key, done) => {
    const cur = { ...(data?.record?.[group] || {}) };
    cur[key] = { ...(cur[key] || {}), done };
    save({ [group]: cur });
  };

  if (!data) return null;
  return (
    <Section title="Start tomorrow — one flow, one board, one shift" tid="method-launch"
             hint={`${content.start_rule} ${data.progress.steps_done}/${data.progress.steps_total} launch steps · ${data.progress.week_one_done}/${data.progress.week_one_total} week-one checks.`}>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input value={form.flow} onChange={(e) => setForm((p) => ({ ...p, flow: e.target.value }))}
               placeholder="The one flow" className="h-9 text-sm" data-testid="method-launch-flow" />
        <Input value={form.deadline} onChange={(e) => setForm((p) => ({ ...p, deadline: e.target.value }))}
               placeholder="Its useful deadline" className="h-9 text-sm" data-testid="method-launch-deadline" />
        <Input value={form.coordinator} onChange={(e) => setForm((p) => ({ ...p, coordinator: e.target.value }))}
               placeholder="Who coordinates it" className="h-9 text-sm" data-testid="method-launch-coordinator" />
      </div>
      <Button size="sm" variant="outline" onClick={() => save()} data-testid="method-launch-save">Save the pilot</Button>

      <div className="grid gap-2 sm:grid-cols-2">
        {content.first_shift_launch.map((s) => {
          const done = data.record?.steps?.[s.key]?.done;
          return (
            <button key={s.key} onClick={() => toggle('steps', s.key, !done)}
                    className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${done ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-muted/60 hover:border-primary/40'}`}
                    data-testid={`method-launch-step-${s.key}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{s.step} · {s.name}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{s.text}</p>
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-xs">
          <thead><tr className="text-muted-foreground">{['When', 'Do this', 'Floor language', 'Evidence'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {content.first_shift_plan.map((p) => (
              <tr key={p.key} className="border-t border-muted/60 align-top" data-testid={`method-launch-plan-${p.key}`}>
                <td className="px-2 py-2 font-medium text-foreground">{p.when}</td>
                <td className="px-2 py-2 text-muted-foreground">{p.do}</td>
                <td className="px-2 py-2 italic text-muted-foreground">“{p.floor}”</td>
                <td className="px-2 py-2 text-muted-foreground">{p.evidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-foreground">Success after one week</p>
        {content.week_one_success.map((w) => {
          const done = data.record?.week_one?.[w.key]?.done;
          return (
            <label key={w.key} className="flex cursor-pointer items-start gap-2 text-[11px] text-muted-foreground" data-testid={`method-launch-week-${w.key}`}>
              <input type="checkbox" checked={!!done} onChange={(e) => toggle('week_one', w.key, e.target.checked)}
                     data-testid={`method-launch-week-check-${w.key}`} />
              <span>{w.text}</span>
            </label>
          );
        })}
      </div>
    </Section>
  );
};

// ─── Maturity check, with evidence, commitment and PDF export ────────
export const MaturityCard = ({ content, maturity, onSaved }) => {
  const [answers, setAnswers] = useState({});
  const [evidence, setEvidence] = useState({});
  const [commitment, setCommitment] = useState({ move: 'K', situation: '', leader: '', first_practice_date: '', evidence: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await submitMaturity({ answers, evidence, commitment: commitment.situation ? commitment : {} });
      toast.success(`Score ${r.score}/20 · ${r.band}`);
      setAnswers({}); setEvidence({});
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const exportPdf = async (which = 'latest') => {
    try { await downloadMaturityPdf(which); toast.success('PDF exported'); }
    catch (e) { toast.error(e.message); }
  };

  const latest = maturity?.latest;
  return (
    <Card data-testid="method-maturity">
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">KANRI maturity check</CardTitle>
          <p className="text-xs text-muted-foreground">{content.maturity_instruction}</p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => exportPdf('blank')} data-testid="method-maturity-export-blank">
            <Printer className="mr-1 h-4 w-4" />Blank form
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportPdf('latest')} disabled={!latest} data-testid="method-maturity-export">
            <Printer className="mr-1 h-4 w-4" />Export answers
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-muted-foreground" data-testid="method-maturity-export-note">
          <strong className="text-foreground">Blank form</strong> prints an empty check to fill in by hand (tick boxes,
          evidence column, score line and commitment lines). <strong className="text-foreground">Export answers</strong>{' '}
          prints the last check you scored here. Both are watermarked 改善管理 and locked — printable and readable, not
          editable or copyable.
        </p>
        {latest && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="method-maturity-latest">
            <p className="text-sm font-semibold text-foreground">{latest.score}/20 · {latest.band}</p>
            <p className="text-[11px] text-muted-foreground">{latest.band_text || content.maturity_band_text[latest.band]}</p>
            {latest.weakest_move && <p className="mt-0.5 text-[11px] text-primary">Weakest move: {latest.weakest_move}</p>}
          </div>
        )}

        {content.maturity_statements.map((s) => (
          <div key={s.key} className="rounded-lg border border-muted/60 p-2.5" data-testid={`method-mat-${s.key}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-foreground"><span className="font-bold text-primary">{s.key}</span> {s.statement}</p>
              <div className="flex shrink-0 gap-1">
                {[0, 1, 2].map((v) => (
                  <button key={v} onClick={() => setAnswers((p) => ({ ...p, [s.key]: v }))}
                          className={`h-7 w-7 rounded border text-xs font-semibold cursor-pointer ${answers[s.key] === v ? 'border-primary bg-primary/10 text-primary' : 'border-muted text-muted-foreground'}`}
                          data-testid={`method-mat-${s.key}-${v}`}>{v}</button>
                ))}
              </div>
            </div>
            <Input value={evidence[s.key] || ''} onChange={(e) => setEvidence((p) => ({ ...p, [s.key]: e.target.value }))}
                   placeholder="Evidence / next action" className="mt-2 h-8 text-xs" data-testid={`method-mat-evidence-${s.key}`} />
          </div>
        ))}

        <div className="rounded-lg border border-muted/60 p-3">
          <p className="text-xs font-semibold text-foreground">Commitment</p>
          <p className="text-[11px] text-muted-foreground">{content.maturity_commitment}</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <select value={commitment.move} onChange={(e) => setCommitment((p) => ({ ...p, move: e.target.value }))}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="method-commit-move">
              {content.moves.map((m) => <option key={m.letter} value={m.letter}>{m.letter} · {m.name}</option>)}
            </select>
            <Input value={commitment.situation} onChange={(e) => setCommitment((p) => ({ ...p, situation: e.target.value }))}
                   placeholder="Operating situation" className="h-9 text-sm" data-testid="method-commit-situation" />
            <Input value={commitment.leader} onChange={(e) => setCommitment((p) => ({ ...p, leader: e.target.value }))}
                   placeholder="Leader" className="h-9 text-sm" data-testid="method-commit-leader" />
            <Input type="date" value={commitment.first_practice_date}
                   onChange={(e) => setCommitment((p) => ({ ...p, first_practice_date: e.target.value }))}
                   className="h-9 text-sm" data-testid="method-commit-date" />
          </div>
          <Textarea rows={2} value={commitment.evidence} onChange={(e) => setCommitment((p) => ({ ...p, evidence: e.target.value }))}
                    placeholder="Evidence that will show improvement" className="mt-2 text-sm" data-testid="method-commit-evidence" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={submit} disabled={busy || Object.keys(answers).length < content.maturity_statements.length}
                  data-testid="method-mat-submit">Score it</Button>
          <span className="text-[11px] text-muted-foreground">
            {content.maturity_bands.map((b) => `${b.from}-${b.to} ${b.band}`).join(' · ')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Five failure signals ────────────────────────────────────────────
export const FailureSignalsCard = ({ content }) => {
  const [findings, setFindings] = useState({});
  const log = async () => {
    try { await createFailureReview({ findings }); toast.success('Review logged'); setFindings({}); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Section title="Failure signals" tid="method-failure-signals" hint={content.reset_move}>
      <div className="grid gap-2 sm:grid-cols-2">
        {content.failure_signals.map((s) => (
          <label key={s.key} className="flex cursor-pointer items-start gap-2 rounded-lg border border-muted/60 p-3" data-testid={`method-fail-${s.key}`}>
            <input type="checkbox" className="mt-1" checked={!!findings[s.key]?.found}
                   onChange={(e) => setFindings((p) => ({ ...p, [s.key]: { found: e.target.checked, note: '' } }))}
                   data-testid={`method-fail-check-${s.key}`} />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-foreground">{s.signal}</p>
              <p className="text-[11px] text-muted-foreground">{s.text}</p>
            </div>
          </label>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={log} data-testid="method-fail-submit">Log review</Button>
    </Section>
  );
};

// ─── The brochure, in full ───────────────────────────────────────────
const Block = ({ b }) => {
  if (b.type === 'kicker') return <p className="pt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">{b.text}</p>;
  if (b.type === 'title') return <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">{b.text}</h2>;
  if (b.type === 'subtitle') return <p className="text-sm text-muted-foreground">{b.text}</p>;
  if (b.type === 'h1') return <h3 className="pt-2 text-base font-semibold text-foreground">{b.text}</h3>;
  if (b.type === 'h2') return <h4 className="pt-1 text-sm font-semibold text-foreground">{b.text}</h4>;
  if (b.type === 'small') return <p className="text-[11px] text-muted-foreground/80">{b.text}</p>;
  if (b.type === 'body') return <p className="text-xs text-muted-foreground">{b.text}</p>;
  if (b.type === 'bullets') {
    return (
      <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        {b.items.map((i) => <li key={i}>{i}</li>)}
      </ul>
    );
  }
  if (b.type === 'callout') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
        {b.label && <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{b.label}</p>}
        <p className="text-[11px] text-foreground">{b.text}</p>
      </div>
    );
  }
  if (b.type === 'cards') {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {b.items.map((i, n) => (
          <div key={`${i.label}-${n}`} className="rounded-lg border border-muted/60 p-3">
            {i.label && <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{i.label}</p>}
            <p className="text-[11px] text-muted-foreground">{i.text}</p>
          </div>
        ))}
      </div>
    );
  }
  if (b.type === 'table') {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-xs">
          <thead>
            <tr className="text-muted-foreground">
              {b.head.map((h, n) => <th key={`${h}-${n}`} className="px-2 py-1.5 text-left font-medium uppercase tracking-wide">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, n) => (
              <tr key={n} className="border-t border-muted/60 align-top">
                {r.map((c, m) => (
                  <td key={m} className={`px-2 py-2 ${m === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

// ─── Issue log — who holds which numbered copy ───────────────────────
export const BrochureIssueLog = ({ data, onChanged }) => {
  const rows = data?.rows || [];
  const toggle = async (row) => {
    const next = row.status === 'revoked' ? 'issued' : 'revoked';
    try { await setBrochureIssueStatus(row.id, next); onChanged?.(); toast.success(`Copy ${next}`); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="method-issue-log">
      <CardHeader>
        <CardTitle className="text-base">Issued copies</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every copy issued to someone else is recorded here against the person who received it, with the copy number,
          purpose and date. The brochure PDF itself is delivered exactly as authored, so the record — not a mark on the
          page — is the trace. Revoking keeps the record. Your own copies are unlimited and never land here.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          {[['Issued', data?.summary?.issued ?? 0, 'method-issue-count'],
            ['With undertaking', data?.summary?.with_nda ?? 0, 'method-issue-nda'],
            ['Revoked', data?.summary?.revoked ?? 0, 'method-issue-revoked']].map(([label, value, tid]) => (
            <div key={label} className="rounded-lg border border-muted/60 p-3" data-testid={tid}>
              <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
              <p className="text-[11px] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        {rows.length === 0 && <p className="text-xs text-muted-foreground">No copy has been issued yet.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-muted/60 p-3"
               data-testid={`method-issue-row-${r.copy_no}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {r.copy_no}
                {r.nda && <Badge variant="secondary" className="ml-2 text-[10px]">undertaking sent</Badge>}
                {r.status === 'revoked' && (
                  <Badge className="ml-2 border border-rose-500/40 bg-rose-500/15 text-rose-700 text-[10px]">revoked</Badge>
                )}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {r.recipient} — {r.recipient_org}
                {r.purpose ? ` · ${r.purpose}` : ''}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Issued {(r.issued_at || '').slice(0, 16).replace('T', ' ')} by {r.issued_by} · {r.pages} pages
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`method-issue-revoke-${r.copy_no}`}>
              <ShieldOff className="mr-1 h-3.5 w-3.5" />{r.status === 'revoked' ? 'Restore' : 'Revoke'}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const BrochureLibrary = () => {
  const { user } = useAuth();
  const isRightsHolder = !!(user?.is_master_admin || user?.role === 'master_admin');
  const [blocks, setBlocks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [issues, setIssues] = useState(null);
  const [form, setForm] = useState({ recipient: '', recipientOrg: '', purpose: '', nda: true });

  const loadIssues = useCallback(async () => {
    try { setIssues(await getBrochureIssues()); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { (async () => { try { setBlocks((await getBrochure()).blocks); } catch (e) { toast.error(e.message); } })(); }, []);
  useEffect(() => { loadIssues(); }, [loadIssues]);

  const exportPdf = async () => {
    setBusy(true);
    try {
      await downloadBrochurePdf(form);
      toast.success('Brochure exported and logged');
      setOpen(false);
      setForm({ recipient: '', recipientOrg: '', purpose: '', nda: true });
      await loadIssues();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const downloadOwnCopy = async () => {
    setBusy(true);
    try {
      await downloadBrochurePdf({ personal: true });
      toast.success('Your own copy — unlimited and not logged');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!blocks) return <p className="text-sm text-muted-foreground">Loading the document…</p>;
  return (
    <>
      <BrochureIssueLog data={issues} onChanged={loadIssues} />

      <Card data-testid="method-brochure">
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">The method, in full</CardTitle>
            <p className="text-xs text-muted-foreground">
              The complete K.A.N.R.I. brochure — every section and every table, exactly as written. The exported PDF is
              the authored document delivered byte-for-byte, with nothing added to the pages: it is only locked against
              editing and copying, while printing stays allowed.
              {isRightsHolder
                ? ' Your own copies are unlimited and never appear in the issue log — only copies issued to someone else are recorded.'
                : ' Every copy is recorded against the person it is issued to, and the confidentiality undertaking is delivered with it as a separate signature sheet.'}
            </p>
          </div>
          {isRightsHolder ? (
            <div className="flex shrink-0 flex-col items-end gap-2">
              <Button size="sm" onClick={downloadOwnCopy} disabled={busy} data-testid="method-brochure-own-copy">
                <Printer className="mr-1 h-4 w-4" />{busy ? 'Building…' : 'Download my copy'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="method-brochure-export">
                Issue to someone
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="method-brochure-export">
              <Printer className="mr-1 h-4 w-4" />Export PDF
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3" data-testid="method-brochure-body">
          {blocks.map((b, i) => <Block key={i} b={b} />)}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)]" data-testid="method-brochure-issue-dialog">
          <DialogHeader>
            <DialogTitle>Issue a licensed copy</DialogTitle>
            <DialogDescription>
              The copy is numbered and logged against this recipient, so a leak traces straight back. For your own
              reading, use “Download my copy” instead — those are unlimited and never logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[['recipient', 'Recipient name'], ['recipientOrg', 'Organisation'],
              ['purpose', 'Purpose (optional) — e.g. pilot discussion']].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium text-foreground">{label}</span>
                <Input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                       className="mt-1 h-9 text-sm" data-testid={`method-issue-${k}`} />
              </label>
            ))}
            <label className="flex cursor-pointer items-start gap-2">
              <input type="checkbox" className="mt-1" checked={isRightsHolder ? form.nda : true}
                     disabled={!isRightsHolder}
                     onChange={(e) => setForm((p) => ({ ...p, nda: e.target.checked }))}
                     data-testid="method-issue-nda-toggle" />
              <span className="text-xs text-foreground">
                Include the confidentiality undertaking — a separate signature sheet the recipient accepts before
                reading, referencing the copy number and EU trade-secret law. It downloads as its own file so the
                brochure itself stays exactly as authored.
                {!isRightsHolder && ' Required on every copy issued from this account.'}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={exportPdf} disabled={busy} data-testid="method-issue-submit">
              {busy ? 'Building…' : 'Export and log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { Section, Stat };
